import { useNavigate } from 'react-router-dom'
import { Button, Text } from '@percent/lemonade'
import { useNativeApply } from '@percent/cause-dashboard/common/components/nativeApply'
import {
  MarketplaceProgrammeVisitedSource,
  OriginCategory
} from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/marketplaceAnalytics.types'
import type { SpotlightBannerCta } from '@percent/cause-dashboard/storyblok/home/resolveSpotlightBannerCta'
import styles from './SpotlightBanner.module.scss'

export type { SpotlightBannerCta }

export type SpotlightBannerProps = {
  preTitle?: string
  title: string
  description?: string
  ctaLabel?: string
  cta?: SpotlightBannerCta
  backgroundImage?: { src: string; alt?: string }
  onCtaClick?: () => void
}

function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href.trim())
}

export function SpotlightBanner({
  preTitle,
  title,
  description,
  ctaLabel,
  cta,
  backgroundImage,
  onCtaClick
}: Readonly<SpotlightBannerProps>) {
  const navigate = useNavigate()
  const { openNativeApply } = useNativeApply()

  const onButtonClick = () => {
    if (!cta) {
      return
    }

    onCtaClick?.()

    if (cta.kind === 'native_apply') {
      openNativeApply({
        partnerKey: cta.partnerKey,
        modalContext: cta.modalContext,
        tracking: {
          programmeOfferName: cta.modalContext.title,
          source: MarketplaceProgrammeVisitedSource.Banner,
          originCategory: OriginCategory.Dashboard
        }
      })
      return
    }

    const { href } = cta
    if (isExternalHref(href)) {
      window.open(href, '_blank', 'noopener,noreferrer')
    } else {
      navigate(href.startsWith('/') ? href : `/${href}`)
      window.scrollTo(0, 0)
    }
  }

  const hasCtaLabel = Boolean(ctaLabel?.trim())
  const showButton = hasCtaLabel && Boolean(cta)

  return (
    <div
      className={styles.spotlightBanner}
      style={backgroundImage?.src ? { backgroundImage: `url(${backgroundImage.src})` } : undefined}
    >
      {preTitle && (
        <Text size="medium" fontWeight="semiBold" align="center" className={styles.preTitle} color="#8B4AFF">
          {preTitle}
        </Text>
      )}
      <span className={styles.title}>{title}</span>
      {description && (
        <Text size="large" align="center" className={styles.description}>
          {description}
        </Text>
      )}
      {showButton && (
        <div className={styles.buttonWrapper}>
          <Button variant="primary" onClick={onButtonClick}>
            {ctaLabel}
          </Button>
        </div>
      )}
    </div>
  )
}
